import {
  Badge,
  Button,
  Card,
  Collapse,
  Divider,
  Checkbox,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useState } from 'react';
import { JURISDICTION_LIST } from '@/lib/constants';
import {
  TCustomerSelections,
  TSimulationInput,
  TSimulationReport,
  TSimulationSelection,
  useOfferSelectionStructure,
  useSimulateCompanySelection,
} from '@/models/offers';
import { isPackageBlock } from '@/models/commercial-proposals/blocks.store';
import { PERMISSIONS, useHasPermission } from '@/models/permissions';
import Title from './Title';

const COUNTRY_OPTIONS = JURISDICTION_LIST.map(({ code, title }) => ({
  value: code,
  label: `${title} (${code})`,
}));

const COUNTRY_MAP = Object.fromEntries(
  JURISDICTION_LIST.map(({ code, title }) => [code, title]),
);

const getCountryName = (code: string) => COUNTRY_MAP[code] || code;

const formatVatRate = (rate: number) => `${(rate * 100).toFixed(0)}%`;

const getExclusionReason = (ruleType: string, ruleKey: string): string => {
  switch (ruleType) {
    case 'jurisdiction':
      return `Cannot invoice services in ${getCountryName(ruleKey)}`;
    case 'self_dealing_jurisdiction':
      return `Cannot register a ${getCountryName(ruleKey)} company from its own jurisdiction`;
    case 'self_dealing_payer':
      return `Cannot invoice a payer in its own country (${getCountryName(ruleKey)})`;
    case 'industry':
      return `Cannot serve ${ruleKey} industry`;
    case 'paying_party_country':
      return `Restricted for clients from ${getCountryName(ruleKey)}`;
    case 'payment_method':
      return `Does not support ${ruleKey} payments`;
    case 'service_category':
      return `Cannot provide ${ruleKey} services`;
    default:
      return `${ruleType}: ${ruleKey}`;
  }
};

const NO_TIER = 'none';

/**
 * Deterministic fingerprint of everything the selection is derived from, so a
 * changed structure always remounts the form rather than leaving stale choices
 * pointing at services the new structure may not offer.
 */
function structureSignature(
  structure: TCustomerSelections,
  hasCustomerChoices: boolean,
): string {
  const source = JSON.stringify({ structure, hasCustomerChoices });
  let hash = 5381;
  for (let i = 0; i < source.length; i++) {
    hash = ((hash << 5) + hash + source.charCodeAt(i)) | 0;
  }
  return String(hash);
}

/**
 * Opens on the customer's actual choices. Where the customer has not engaged
 * at all, falls back to the fullest reading of the offer - top tier of every
 * package block, every service ticked - which is what an unrestricted sale
 * would be. Declining every package is itself a choice, so this must key off
 * whether the customer engaged, never off whether anything ended up selected.
 */
function buildInitialSelection(
  structure: TCustomerSelections,
  hasCustomerChoices: boolean,
): TSimulationSelection {
  return {
    proposals: structure.proposals.map((proposal) => ({
      cpId: proposal.cpId,
      blocks: proposal.blocks.map((block) => {
        if (isPackageBlock(block.type)) {
          return {
            uuid: block.uuid,
            selectedTier: hasCustomerChoices
              ? block.selectedTier
              : block.tierNames.length - 1,
            selectedServiceIds: [],
          };
        }
        return {
          uuid: block.uuid,
          selectedTier: null,
          selectedServiceIds: block.services
            .filter((s) => (hasCustomerChoices ? s.selected : true))
            .map((s) => s.serviceId),
        };
      }),
    })),
    standaloneServiceIds: structure.standaloneServices
      .filter((s) => (hasCustomerChoices ? s.selected : true))
      .map((s) => s.serviceId),
  };
}

export default function SimulateInvoiceCompanySelection({
  offerId,
}: {
  offerId: number;
}) {
  const hasPermission = useHasPermission(PERMISSIONS.RBAC_WRITE);
  const [isOpen, setIsOpen] = useState(false);
  const { data, isPending, isError } = useOfferSelectionStructure(
    offerId,
    isOpen && hasPermission,
  );

  if (!hasPermission) return null;

  return (
    <Card withBorder>
      <Group
        justify="space-between"
        style={{ cursor: 'pointer' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Title>Invoice Company Selection Simulator</Title>
        {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
      </Group>
      <Collapse expanded={isOpen}>
        {isPending && (
          <Text size="xs" c="dimmed" mt="sm">
            Loading offer structure...
          </Text>
        )}
        {isError && (
          <Text size="xs" c="red" mt="sm">
            Could not load the offer's selectable structure.
          </Text>
        )}
        {data && (
          <SimulatorForm
            /* Remount on a new structure so the selection is always built from
               the structure actually on screen. */
            key={`${offerId}:${structureSignature(data.structure, data.hasCustomerChoices)}`}
            offerId={offerId}
            structure={data.structure}
            hasCustomerChoices={data.hasCustomerChoices}
          />
        )}
      </Collapse>
    </Card>
  );
}

function SimulatorForm({
  offerId,
  structure,
  hasCustomerChoices,
}: {
  offerId: number;
  structure: TCustomerSelections;
  hasCustomerChoices: boolean;
}) {
  const mutation = useSimulateCompanySelection();

  const [paymentMethod, setPaymentMethod] = useState<string>('Bank Transfer');
  const [clientType, setClientType] = useState<string>('Individual');
  const [country, setCountry] = useState<string>('');
  const [vatNumber, setVatNumber] = useState<string>('');
  const [currency, setCurrency] = useState<string>('EUR');

  const [selection, setSelection] = useState<TSimulationSelection>(() =>
    buildInitialSelection(structure, hasCustomerChoices),
  );

  const setTier = (cpId: number, uuid: string, tier: number | null) =>
    setSelection((prev) => ({
      ...prev,
      proposals: prev.proposals.map((p) =>
        p.cpId === cpId
          ? {
              ...p,
              blocks: p.blocks.map((b) =>
                b.uuid === uuid ? { ...b, selectedTier: tier } : b,
              ),
            }
          : p,
      ),
    }));

  const toggleBlockService = (
    cpId: number,
    uuid: string,
    serviceId: number,
    checked: boolean,
  ) =>
    setSelection((prev) => ({
      ...prev,
      proposals: prev.proposals.map((p) =>
        p.cpId === cpId
          ? {
              ...p,
              blocks: p.blocks.map((b) =>
                b.uuid === uuid
                  ? {
                      ...b,
                      selectedServiceIds: checked
                        ? [...b.selectedServiceIds, serviceId]
                        : b.selectedServiceIds.filter((id) => id !== serviceId),
                    }
                  : b,
              ),
            }
          : p,
      ),
    }));

  const toggleStandalone = (serviceId: number, checked: boolean) =>
    setSelection((prev) => ({
      ...prev,
      standaloneServiceIds: checked
        ? [...prev.standaloneServiceIds, serviceId]
        : prev.standaloneServiceIds.filter((id) => id !== serviceId),
    }));

  const blockSelection = (cpId: number, uuid: string) =>
    selection.proposals
      .find((p) => p.cpId === cpId)
      ?.blocks.find((b) => b.uuid === uuid);

  const handleSubmit = () => {
    if (!country) return;

    const input: TSimulationInput = {
      offerId,
      paymentMethod: paymentMethod as 'Bank Transfer' | 'Cryptocurrency',
      clientType: clientType as 'Individual' | 'Legal Entity',
      country,
      currency:
        paymentMethod === 'Bank Transfer'
          ? (currency as 'EUR' | 'USD')
          : undefined,
      vatNumber: clientType === 'Legal Entity' ? vatNumber : undefined,
      selection,
    };

    mutation.mutate(input);
  };

  return (
    <Stack gap="sm" mt="sm">
      <Select
        label="Payment Method"
        size="xs"
        value={paymentMethod}
        onChange={(v) => v && setPaymentMethod(v)}
        data={[
          { value: 'Bank Transfer', label: 'Bank Transfer (Fiat)' },
          { value: 'Cryptocurrency', label: 'Cryptocurrency' },
        ]}
      />

      <Select
        label="Client Type"
        size="xs"
        value={clientType}
        onChange={(v) => v && setClientType(v)}
        data={[
          { value: 'Individual', label: 'Individual' },
          { value: 'Legal Entity', label: 'Legal Entity' },
        ]}
      />

      {structure && (
        <Stack gap="xs">
          <Text size="xs" fw={600}>
            Customer Selection
          </Text>
          <Text size="xs" c="dimmed">
            Mirrors what the customer chooses: one package per tiered block,
            individual services elsewhere.
          </Text>

          {structure.proposals.map((proposal) => (
            <Card withBorder p="xs" key={proposal.cpId}>
              <Text size="xs" fw={500}>
                {proposal.cpName} ({proposal.jurisdiction})
              </Text>
              <Stack gap="xs" mt="xs">
                {proposal.blocks.map((block) => {
                  const chosen = blockSelection(proposal.cpId, block.uuid);
                  if (isPackageBlock(block.type)) {
                    return (
                      <Select
                        key={block.uuid}
                        label={block.title}
                        size="xs"
                        value={
                          chosen?.selectedTier === null ||
                          chosen?.selectedTier === undefined
                            ? NO_TIER
                            : String(chosen.selectedTier)
                        }
                        onChange={(v) =>
                          setTier(
                            proposal.cpId,
                            block.uuid,
                            v === null || v === NO_TIER ? null : Number(v),
                          )
                        }
                        data={[
                          { value: NO_TIER, label: 'Not purchased' },
                          ...block.tierNames.map((name, tier) => ({
                            value: String(tier),
                            label: name,
                          })),
                        ]}
                      />
                    );
                  }
                  return (
                    <Stack gap={4} key={block.uuid}>
                      <Text size="xs" fw={500}>
                        {block.title}
                      </Text>
                      {block.services.map((svc) => (
                        <Checkbox
                          key={svc.serviceId}
                          size="xs"
                          label={svc.serviceName}
                          checked={
                            chosen?.selectedServiceIds.includes(
                              svc.serviceId,
                            ) ?? false
                          }
                          onChange={(e) =>
                            toggleBlockService(
                              proposal.cpId,
                              block.uuid,
                              svc.serviceId,
                              e.currentTarget.checked,
                            )
                          }
                        />
                      ))}
                    </Stack>
                  );
                })}
              </Stack>
            </Card>
          ))}

          {structure.standaloneServices.length > 0 && (
            <Card withBorder p="xs">
              <Text size="xs" fw={500}>
                Standalone Services
              </Text>
              <Stack gap={4} mt="xs">
                {structure.standaloneServices.map((svc) => (
                  <Checkbox
                    key={svc.serviceId}
                    size="xs"
                    label={svc.serviceName}
                    checked={selection.standaloneServiceIds.includes(
                      svc.serviceId,
                    )}
                    onChange={(e) =>
                      toggleStandalone(svc.serviceId, e.currentTarget.checked)
                    }
                  />
                ))}
              </Stack>
            </Card>
          )}
        </Stack>
      )}

      <Select
        label="Paying Party Country"
        size="xs"
        value={country}
        onChange={(v) => setCountry(v || '')}
        data={COUNTRY_OPTIONS}
        searchable
        clearable
        placeholder="Select country"
      />

      {clientType === 'Legal Entity' && (
        <TextInput
          label="VAT Number"
          size="xs"
          value={vatNumber}
          onChange={(e) => setVatNumber(e.target.value)}
          placeholder="Optional"
        />
      )}

      {paymentMethod === 'Bank Transfer' && (
        <Select
          label="Currency"
          size="xs"
          value={currency}
          onChange={(v) => v && setCurrency(v)}
          data={[
            { value: 'EUR', label: 'EUR' },
            { value: 'USD', label: 'USD' },
          ]}
        />
      )}

      <Button
        size="xs"
        onClick={handleSubmit}
        loading={mutation.isPending}
        disabled={!country}
      >
        Simulate Selection
      </Button>

      {mutation.isError && (
        <Text size="xs" c="red">
          Error: {String(mutation.error)}
        </Text>
      )}

      {mutation.isSuccess && mutation.data && (
        <SimulationResult data={mutation.data} />
      )}
    </Stack>
  );
}

function SimulationResult({ data }: { data: TSimulationReport }) {
  const { report, proposalContext, agents, simulatedServiceIds } = data;
  const registrationJurisdictions = data.registrationJurisdictions;

  return (
    <Stack gap="sm" mt="md">
      <Divider />

      <Text size="xs" c="dimmed">
        Simulated over {simulatedServiceIds.length}{' '}
        {simulatedServiceIds.length === 1 ? 'service' : 'services'} |
        jurisdictions:{' '}
        {report.context.serviceJurisdictions.map(getCountryName).join(', ') ||
          'none'}
        {' | '}company registered in:{' '}
        {registrationJurisdictions.map(getCountryName).join(', ') || 'none'}
      </Text>

      <Text size="xs" fw={600}>
        Selected Company
      </Text>
      <Card withBorder p="xs">
        <Text size="xs" fw={500}>
          {report.selectedCompany.name}
        </Text>
        <Text size="xs" c="dimmed" mt={4}>
          {getCountryName(report.selectedCompany.countryCode)} | Applied VAT:{' '}
          {formatVatRate(report.vatCalculation.appliedRate)} | Priority:{' '}
          {report.selectedCompany.priority}
        </Text>
        {report.usedFallback && (
          <Badge color="orange" size="xs" mt={4}>
            Fallback Used
          </Badge>
        )}
      </Card>

      <Text size="xs" fw={600}>
        Selection Reason
      </Text>
      <Card withBorder p="xs">
        <Text size="xs">
          {report.usedFallback ? (
            <>
              <strong>Fallback applied:</strong> All eligible companies were
              excluded by business rules. Using {report.selectedCompany.name} (
              {report.selectedCompany.countryCode}) as default.
            </>
          ) : (
            <>
              <strong>{report.selectedCompany.name}</strong> (
              {report.selectedCompany.countryCode}) was selected as the{' '}
              <strong>
                highest-priority ({report.selectedCompany.priority})
              </strong>{' '}
              eligible company.
            </>
          )}
        </Text>
        {report.exclusions.length > 0 && (
          <Stack gap={2} mt="xs">
            <Text size="xs" c="dimmed" fw={500}>
              Excluded companies:
            </Text>
            {Object.entries(
              report.exclusions.reduce(
                (acc, exc) => {
                  const key = exc.companyName;
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(exc);
                  return acc;
                },
                {} as Record<string, typeof report.exclusions>,
              ),
            ).map(([name, exclusions]) => (
              <Text key={name} size="xs" c="dimmed">
                • {name}:{' '}
                {exclusions
                  .map((exc) => getExclusionReason(exc.ruleType, exc.ruleKey))
                  .join('; ')}
              </Text>
            ))}
          </Stack>
        )}
      </Card>

      <Text size="xs" fw={600}>
        VAT Calculation
      </Text>
      <Card withBorder p="xs">
        <Text size="xs">
          Applied Rate:{' '}
          <strong>{formatVatRate(report.vatCalculation.appliedRate)}</strong>
        </Text>
        <Text size="xs" c="dimmed">
          {report.vatCalculation.isDomestic
            ? 'Domestic'
            : 'Cross-border (export)'}{' '}
          | Buyer: {getCountryName(report.vatCalculation.buyerCountry)} |
          Seller: {getCountryName(report.vatCalculation.sellerCountry)}
        </Text>
      </Card>

      <Text size="xs" fw={600}>
        Offer Context
      </Text>
      <Card withBorder p="xs">
        <Text size="xs">
          Jurisdictions:{' '}
          {proposalContext.jurisdictions.map(getCountryName).join(', ') ||
            'None'}
        </Text>
        <Text size="xs">
          Categories: {proposalContext.serviceCategories.join(', ') || 'None'}
        </Text>
        <Text size="xs">
          Industries: {proposalContext.industries.join(', ') || 'None'}
        </Text>
      </Card>

      {report.exclusions.length > 0 && (
        <>
          <Text size="xs" fw={600}>
            Exclusions Applied ({report.exclusions.length})
          </Text>
          <Table withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ fontSize: '10px' }}>Company</Table.Th>
                <Table.Th style={{ fontSize: '10px' }}>Rule Type</Table.Th>
                <Table.Th style={{ fontSize: '10px' }}>Rule Key</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {report.exclusions.map((exc, idx) => (
                <Table.Tr key={idx}>
                  <Table.Td style={{ fontSize: '10px' }}>
                    {exc.companyName}
                  </Table.Td>
                  <Table.Td style={{ fontSize: '10px' }}>
                    {exc.ruleType.replace(/_/g, ' ')}
                  </Table.Td>
                  <Table.Td style={{ fontSize: '10px' }}>
                    {exc.ruleKey}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}

      <Text size="xs" fw={600}>
        Eligible Companies ({report.eligibleCompanies.length})
      </Text>
      <Stack gap={4}>
        {report.eligibleCompanies.map((c) => (
          <Text
            key={c.id}
            size="xs"
            c={c.id === report.selectedCompany.id ? 'green' : 'dimmed'}
            fw={c.id === report.selectedCompany.id ? 500 : 400}
          >
            {c.name} (Priority {c.priority})
            {c.id === report.selectedCompany.id && ' - Selected'}
          </Text>
        ))}
        {report.eligibleCompanies.length === 0 && (
          <Text size="xs" c="dimmed">
            None - using fallback
          </Text>
        )}
      </Stack>

      {agents.length > 0 && (
        <>
          <Text size="xs" fw={600}>
            Agent Payment Capabilities ({agents.length})
          </Text>
          <Table withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ fontSize: '10px' }}>Agent</Table.Th>
                <Table.Th style={{ fontSize: '10px' }}>Fiat</Table.Th>
                <Table.Th style={{ fontSize: '10px' }}>Crypto</Table.Th>
                <Table.Th style={{ fontSize: '10px' }}>Services</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {agents.map((agent) => (
                <Table.Tr key={agent.id}>
                  <Table.Td style={{ fontSize: '10px' }}>{agent.name}</Table.Td>
                  <Table.Td style={{ fontSize: '10px' }}>
                    <Badge
                      size="xs"
                      color={agent.acceptsFiat ? 'green' : 'gray'}
                    >
                      {agent.acceptsFiat ? 'Yes' : 'No'}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ fontSize: '10px' }}>
                    <Badge
                      size="xs"
                      color={agent.acceptsCrypto ? 'green' : 'gray'}
                    >
                      {agent.acceptsCrypto ? 'Yes' : 'No'}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ fontSize: '10px' }}>
                    {agent.services.join(', ')}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}
    </Stack>
  );
}
